/**
 * Shared inline icon components — Lucide geometry, copied into the
 * codebase so we don't pull the lucide-react package as a dependency.
 *
 * ## Why a shared module
 *
 * The codebase convention is to inline icons at their consuming
 * component (see `notes-icon-button.tsx` for the rationale). That
 * convention scales fine when an icon is single-purpose, but the
 * icons in this file are CROSS-CUTTING — Settings appears on the
 * routines page header AND in the exercise picker; Trophy appears
 * on the history list AND in the active-workout PR badge AND on
 * bodyweight goal-reached surfaces. Inlining at every site would
 * mean copying the same SVG path 3+ times, with the usual drift
 * risk when one site needs a tweak the others don't get.
 *
 * Single-use icons stay inlined at their consumer (FolderIcon in
 * routines-header.tsx, FolderOpenIcon in routines-header.tsx,
 * StickyNoteIcon in notes-icon-button.tsx, BulletIcon /
 * Undo2Icon / Redo2Icon in exercise-notes-editor.tsx).
 * Only the multi-call-site icons live
 * here.
 *
 * ## Why we replaced color emojis
 *
 * Color emoji rendering varies wildly across platforms (Apple's 3D
 * gear vs Microsoft's flat outline vs Google's blob style). The
 * resulting visual is jarring next to the rest of the app's
 * orange-on-dark brand language — a polished interface dotted with
 * cartoonish 3D glyphs reads as inconsistent. Lucide-style stroked
 * SVGs render identically on every platform, scale to any pixel
 * density without an extra asset request, and inherit
 * `currentColor` so they pick up the surrounding text color
 * automatically (a brand-orange trophy chip needs no separate
 * fill-color override).
 *
 * ## Source
 *
 * Path data copied verbatim from Lucide v0 source so a future
 * Lucide rev can be re-synced cleanly. License: ISC, attribution
 * preserved by this comment block.
 */

type IconProps = {
  /** Size + colour control via Tailwind className. Defaults to
   *  `w-4 h-4` (16px) — the standard caption-icon size used
   *  throughout the app. Pass a different className to scale up for
   *  hero-stat tiles or down for inline-text glyphs. */
  className?: string;
};

/** Wraps the common Lucide stroke defaults so each icon's body
 *  only declares its unique paths. `aria-hidden` on every icon —
 *  these are decorative; the surrounding button / label carries
 *  the accessible name. */
function IconBase({
  className = "w-4 h-4",
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {children}
    </svg>
  );
}

/** Stopwatch — the app's "this is a duration" glyph. Extracted from the
 *  active-workout header's inline elapsed-time SVG so every time readout
 *  (workout elapsed, History month total, est-time chips) uses one logo.
 *  Circle body + centre hand + top stem. */
export function StopwatchIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <circle cx="12" cy="13" r="8" />
      <path d="M12 9v4l2.5 2.5" />
      <path d="M9 2h6" />
    </IconBase>
  );
}

/** Settings cog. Replaces `⚙` in routines header (Manage Exercises
 *  button) + exercise picker (Manage library link). */
export function SettingsIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </IconBase>
  );
}

/** Flame. Replaces `🔥` for streak chips on the history page. */
/** Clock with a counter-clockwise arrow - "the past". Moved here from
 *  `tab-bar.tsx` when History gave its bottom-tab slot to Macros: it was a
 *  private 22px tab glyph with one consumer, and its consumer is now the
 *  History door on /progress, which is not in that file. Same geometry,
 *  re-expressed through IconBase so it takes a className like the rest. */
export function HistoryRewindIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
      <path d="M12 7v5l4 2" />
    </IconBase>
  );
}

export function FlameIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
    </IconBase>
  );
}

/** Trophy. Replaces `🏆` for PR chips across history + active
 *  workout, and `🎉` for "Goal reached" on bodyweight surfaces
 *  (achievement reads more honest as a trophy than a party
 *  popper — a consistent visual vocabulary for "you accomplished
 *  the thing"). */
export function TrophyIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M4 22h16" />
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
    </IconBase>
  );
}

/** Dumbbell for the workouts-this-week tile on the history page. Matches the
 *  "Workouts" tab-bar glyph (DumbbellTabIcon in tab-bar.tsx) so the same icon
 *  means "workouts" everywhere - rect geometry copied from there, just
 *  className-sized instead of the tab's fixed 26px (keep the two in sync if
 *  either changes). fill (not stroke) per the tab icon.
 *
 *  It was briefly rotated -45deg to separate it from the brand mark on the Log
 *  tab, which is also a barbell. Reverted on sight: diagonal read as the glyph
 *  pointing upwards rather than as a dumbbell. The separation now rests on the
 *  things that are already true - the Log mark is 2.5x the size, two-tone with
 *  an orange R, and the only tab without a caption. */
export function DumbbellIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" className={className} fill="currentColor" aria-hidden>
      <rect x="0.2" y="6.75" width="1.3" height="2.5" rx="0.4" />
      <rect x="2.0" y="5.25" width="1.3" height="5.5" rx="0.4" />
      <rect x="3.8" y="4.25" width="1.3" height="7.5" rx="0.5" />
      <rect x="5.1" y="7.2" width="5.8" height="1.6" rx="0.6" />
      <rect x="10.9" y="4.25" width="1.3" height="7.5" rx="0.5" />
      <rect x="12.7" y="5.25" width="1.3" height="5.5" rx="0.4" />
      <rect x="14.5" y="6.75" width="1.3" height="2.5" rx="0.4" />
    </svg>
  );
}

/** X (close). Replaces `✕` on the templates panel close button,
 *  the library row's dismiss-message button, and the RowActions
 *  dismiss tap. */
export function XIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </IconBase>
  );
}

/** Lucide eye-off — the "hide this" control on /progress cards and the
 *  bodyweight trend section.
 *
 *  Deliberately NOT an X. Sitting immediately after the pin on a card that
 *  also has a delete path elsewhere in the app, an X reads as "remove /
 *  destroy"; an eye with a slash reads as "stop showing me this", which is
 *  exactly what it does - the lift keeps every set it ever had and still
 *  counts toward volume, the muscle map and history. */
export function EyeOffIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.5 13.5 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
      <path d="m2 2 20 20" />
    </IconBase>
  );
}

/** Lucide check — single-stroke tick mark. Used as the
 *  "completed" indicator at the right edge of logged SetRows
 *  (v9.198). Brand-orange when the row is a confirmed entry;
 *  paired with the rest of the row's dimmed treatment so the
 *  tick is the only saturated detail signalling "this set is
 *  committed".
 *
 *  v9.199 — overrides IconBase's strokeWidth=2 default with a
 *  thicker stroke (3.25) so the tick reads as a confident
 *  "logged" mark rather than a delicate line glyph. Other icons
 *  in the file stay at the lighter default; the check is a
 *  semantic flag, not chrome, so it earns the extra weight. */
export function CheckIcon({ className = "w-4 h-4" }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={3.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/** Lucide delete — the backspace key glyph (hexagon-shape with an
 *  inset ✕). Used on the custom numeric keypad's backspace cell as a
 *  Lucide replacement for the inline `⌫` unicode glyph, so the cell
 *  reads with the same stroked-SVG language as every other icon in
 *  the app rather than as a system-font fallback character. */
export function DeleteIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
      <path d="m18 9-6 6" />
      <path d="m12 9 6 6" />
    </IconBase>
  );
}

/** Lucide rotate-ccw. Sprint 22 v9.102 — undo affordance icon
 *  replacing the "Undo" text button on the delete-set toast.
 *  Compact icon-only treatment fits the new on-top-of-rest-bar
 *  chip shape. */
export function RotateCcwIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
    </IconBase>
  );
}

/** Lucide repeat. The CIRCUIT glyph: a workout of rounds-of-stations.
 *
 *  It is NOT the cycle-schedule mark - a schedule of any shape (every N days,
 *  an N-day cycle, weekly days) wears a CalendarIcon, because a looping arrow
 *  next to "3-DAY CYCLE" and a looping arrow meaning "this is a circuit" on
 *  the row above it are the same sign asserting two things. /workouts holds
 *  that rule; routine-detail-client and shared-routine-preview still use this
 *  glyph for repeats and have not been brought in line. */
export function RepeatIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="m17 2 4 4-4 4" />
      <path d="M3 11v-1a4 4 0 0 1 4-4h14" />
      <path d="m7 22-4-4 4-4" />
      <path d="M21 13v1a4 4 0 0 1-4 4H3" />
    </IconBase>
  );
}

/** Lucide arrow-left-right. The exchange glyph (top arrow points
 *  left, bottom arrow points right) used on the active-workout
 *  "Replace" affordance to swap an exercise for a same-muscle
 *  alternative when the equipment's taken. */
export function ArrowLeftRightIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M8 3 4 7l4 4" />
      <path d="M4 7h16" />
      <path d="m16 21 4-4-4-4" />
      <path d="M20 17H4" />
    </IconBase>
  );
}

/** Lucide skip-forward. The "skip this, move to the next" media glyph,
 *  used on the active-workout "Skip" affordance beside Replace. */
export function SkipForwardIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <polygon points="5 4 15 12 5 20 5 4" />
      <line x1="19" x2="19" y1="5" y2="19" />
    </IconBase>
  );
}

/** Lucide log-out. Sprint 22 v9.97 — replaces the "Sign out" text
 *  button in the app shell header on every non-workout route. The
 *  icon-only treatment matches the cog (also icon-only) so the
 *  right cluster reads as an icon strip rather than icon + text
 *  pair. Aria-label preserves the verb for screen readers. */
export function LogOutIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    </IconBase>
  );
}

/** Pencil. Replaces `✎` for the Edit affordance on the bodyweight
 *  detail page. */
/**
 * The edit affordance on a set row.
 *
 * Drawn at strokeWidth 1.5 rather than IconBase's 2. At the size this appears
 * - inline beside "32 KG x 12 reps" - a 2px stroke is nearly as wide as the
 * gap inside the nib, so the tip closes up into a blob and the pencil stops
 * looking like a pencil. Thinner, and slightly larger, reads as a tool.
 *
 * Its own <svg> rather than a lighter IconBase, deliberately: IconBase sets
 * the weight for every icon in the app, and relighting all of them to fix one
 * would change every screen. Keep this local until there is a reason to
 * revisit the whole set.
 */
export function PencilIcon({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

/** Lucide trash-2. Sprint 19 Step 19: promoted from a single-call-
 *  site inline (was in exercise-card.tsx for the per-set Discard
 *  button) to here, because it's now used at multiple destructive-
 *  action surfaces across the app — per-set Discard, workout-level
 *  Discard, history-row Delete, etc. Single source of truth keeps
 *  the destructive-action visual vocabulary consistent everywhere. */
export function Trash2Icon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M3 6h18" />
      <path d="M19 6v14c0 1.1-.9 2-2 2H7c-1.1 0-2-.9-2-2V6" />
      <path d="M8 6V4c0-1.1.9-2 2-2h4c1.1 0 2 .9 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </IconBase>
  );
}

/** Chevron-left. Replaces the bordered "‹ Section" / "← Section"
 *  back-link pills that previously sat at the top of every detail
 *  page (History detail, Routines detail, Progress sub-pages,
 *  Routines kebab submenu). Single-icon back affordance reduces
 *  chrome at the top of the page; the destination is implicit
 *  (always the parent index page) which is fine because the user
 *  knows where they came from. Hard-coded `<Link href="...">`
 *  destinations preserved at each call site for predictability —
 *  this icon is just the visual replacement for the pill chrome. */
export function ChevronLeftIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="m15 18-6-6 6-6" />
    </IconBase>
  );
}

/** Lucide chevron-right — mirror of ChevronLeftIcon. Used on the
 *  numeric keypad's header as the "swipe right to open calculator"
 *  hint affordance (also tap-able as a fallback). Symmetric with
 *  the back-arrow on the calculator panel so the two surfaces
 *  share the same gesture / chevron vocabulary. */
export function ChevronRightIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="m9 18 6-6-6-6" />
    </IconBase>
  );
}

/** Lucide arrow-right. On /workouts a chevron means "the page stays under
 *  you" - one opens a plan, two peek a workout - so the way OUT of the page
 *  cannot be a third chevron. An arrow says the page changes. Same family,
 *  one job each. Not an external-link glyph, which means "leaves the app". */
export function ArrowRightIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </IconBase>
  );
}

/** Lucide chevron-down. Used as the "expand a collapsed section"
 *  affordance — VolumeMapCard's collapse-toggle being the first
 *  consumer. Paired with ChevronUpIcon as the "collapse the
 *  currently-expanded section" affordance. */
export function ChevronDownIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="m6 9 6 6 6-6" />
    </IconBase>
  );
}

/**
 * Lucide chevrons-down — a DOUBLE chevron, and the only reason it exists is to
 * tell two disclosures apart.
 *
 * /workouts stacks two of them: a plan opens to reveal its workouts, and a
 * workout opens to reveal its exercises. Both were single chevrons pointing
 * right when shut and down when open, so they read as one control doing one
 * thing when they do not. Differentiating by DIRECTION instead does not work -
 * every combination of the two states co-occurs, including the page's default
 * (plan open, workouts shut), so any assignment of two directions to two
 * controls collides somewhere.
 *
 * Doubling the glyph is the one axis that never collides: single means "this
 * container", double means "what is inside this workout", at any rotation.
 *
 * At 16px the two strokes sit ~1.3px apart, which is fine on a phone and
 * smears on a 1x desktop preview. Judge it on the device, and if it genuinely
 * merges step the glyph up rather than thinning the stroke - IconBase sets
 * strokeWidth 2 for the whole set and this icon must not be the exception.
 */
export function ChevronsDownIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="m7 6 5 5 5-5" />
      <path d="m7 13 5 5 5-5" />
    </IconBase>
  );
}

/** Lucide chevron-up — mirror of ChevronDownIcon. Used at the
 *  same disclosure-toggle positions to indicate "expanded; tap to
 *  collapse." */
export function ChevronUpIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="m18 15-6-6-6 6" />
    </IconBase>
  );
}

/** Info circle. Replaces inline always-on educational/help text
 *  at toggleable disclosure surfaces — first used on the Progress
 *  page to tuck the BEST/EST cardio explanation behind a tap. The
 *  always-on text earned its keep for first-time users but read as
 *  noise for returning users; the icon-then-disclosure pattern
 *  serves both. Same Lucide stroke vocabulary as the rest of the
 *  shared icons. */
export function InfoIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </IconBase>
  );
}

/** Lucide medal — circular medal with hanging ribbon, suggests
 *  "achievement / award." Used on the Progress page's PR Timeline
 *  link as a more compact alternative to the trophy icon (which
 *  read as ambiguous award-iconography at small sizes and felt
 *  over-celebratory for a sibling-page nav link). Path data
 *  copied from Lucide v0 medal source. */
export function MedalIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M7.21 15 2.66 7.14a2 2 0 0 1 .13-2.2L4.4 2.8A2 2 0 0 1 6 2h12a2 2 0 0 1 1.6.8l1.6 2.14a2 2 0 0 1 .14 2.2L16.79 15" />
      <path d="M11 12 5.12 2.2" />
      <path d="m13 12 5.88-9.8" />
      <path d="M8 7h8" />
      <circle cx="12" cy="17" r="5" />
      <path d="M12 18v-2h-.5" />
    </IconBase>
  );
}

/** Lucide wrench — adjustable spanner motif, signals "tooling /
 *  settings under the hood." Used on the admin index header as the
 *  affordance into Global App Settings. Lighter glyph than the
 *  earlier globe-lock attempt (which read as too visually heavy
 *  next to the smaller square-play / user-check icons in the same
 *  cluster). Wrench is the standard "admin / config" icon across
 *  most dashboards — instantly readable, no learning cost. */
export function WrenchIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </IconBase>
  );
}

/** Lucide play — outlined right-pointing triangle. Used on the
 *  admin index header for the exercise demo-URL editor (Sprint D4).
 *  Lighter weight than the earlier square-play (which carried the
 *  surrounding bounding-box stroke as well as the inner triangle —
 *  visually heavy at 18px and read as a TV/video panel rather than
 *  the clean "play" verb we want). The bare triangle reads more
 *  unambiguously as "demo videos." */
export function PlayIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <polygon points="6 3 20 12 6 21 6 3" />
    </IconBase>
  );
}

/** Lucide user-x — user silhouette with an X overlay. Used on the
 *  admin per-user detail's "Suspend account" affordance. Visual
 *  parity with the icon-only Delete-user / Routines-delete /
 *  user-facing Delete-account triggers — every destructive entry
 *  point across the app now opens via an icon, not a text button.
 *  X overlay communicates "ban this user" more directly than the
 *  generic ban circle would; user-x is the standard glyph for
 *  user-suspension across most dashboards. */
export function UserXIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="17" y1="8" x2="22" y2="13" />
      <line x1="22" y1="8" x2="17" y2="13" />
    </IconBase>
  );
}

/** Lucide message-circle-question — speech bubble with a small
 *  question mark inside. Used on the global shell header as the
 *  "Send feedback" affordance. The earlier message-square glyph
 *  was too generic (could read as chat / comments / messages);
 *  the embedded `?` signals "ask us / tell us something" directly.
 *  Universal "talk to us" iconography across most SaaS dashboards.
 */
export function MessageCircleQuestionIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
      <path d="M9.1 9a3 3 0 0 1 5.82 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </IconBase>
  );
}

/** Lucide message-circle — plain speech bubble (no question mark).
 *  v9.267 — replaced MessageCircleQuestionIcon as the global-shell
 *  "Send feedback" affordance. The question-mark variant felt
 *  ambiguous next to the new tour entry; a plain bubble reads
 *  unambiguously as "talk to us" while the lightbulb does the
 *  "ask us how it works" job.
 *  MessageCircleQuestionIcon stays in this file (still used by
 *  admin pages — feedback-table.tsx + query-client.tsx — for the
 *  feedback-management iconography there). */
export function MessageCircleIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
    </IconBase>
  );
}

/** Lucide lightbulb — bulb silhouette with a base. v9.267 — used
 *  on the global-shell tour entry button. Chosen over a
 *  message-circle-question to sit cleanly next to the plain
 *  message-circle feedback button (different glyph families =
 *  zero side-by-side confusion). Universal "tip / discover /
 *  learn" semantic across SaaS chrome (Intercom, Linear,
 *  Notion all use lightbulb-family glyphs for product tours). */
export function LightbulbIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
      <path d="M9 18h6" />
      <path d="M10 22h4" />
    </IconBase>
  );
}

/** Lucide database — three-cylinder stack, the classic "DB"
 *  glyph. Used on the admin index for the data-query / analytics
 *  surface. Communicates "queries against the live data" more
 *  directly than a generic chart icon would. */
export function DatabaseIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5v14a9 3 0 0 0 18 0V5" />
      <path d="M3 12a9 3 0 0 0 18 0" />
    </IconBase>
  );
}

/** Lucide download — a down-arrow into a tray. Used on the Settings
 *  "Download my data" affordance and the inline "Download a copy
 *  first" affordance inside the Delete Account confirm. Single icon
 *  vocabulary across both call sites — same gesture, same glyph. */
export function DownloadIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </IconBase>
  );
}

/** Lucide upload — the same tray as DownloadIcon with the arrow rising
 *  OUT of it instead of dropping in (shaft terminates cleanly at the tray
 *  mouth, mirroring DownloadIcon). Visually the iOS "Share" affordance, so
 *  it heads the landing PWA install nudge's "tap Share, then Add to Home
 *  Screen" instruction — hence exported as ShareIcon. Geometry is Lucide
 *  `upload`, copied verbatim; Lucide's own `share` is the unrelated
 *  three-node connected-dots glyph, not this box-and-arrow. */
export function ShareIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </IconBase>
  );
}

/** Lucide user-check — user silhouette with a small check overlay.
 *  Used on the admin index header for the "Preview onboarding"
 *  affordance: lets an admin walk through the signup wizard from
 *  inside their existing account without having to create a
 *  throwaway user. Read as "verify the new-user experience" rather
 *  than the more ambiguous user-plus / user-cog icons. */
export function UserCheckIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <polyline points="16 11 18 13 22 9" />
    </IconBase>
  );
}

/** Lucide trending-up — line going up + arrow. Used on the admin
 *  index header for the Insights dashboard affordance. Communicates
 *  "trendlines / growth analytics" without needing a label. */
export function TrendingUpIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </IconBase>
  );
}

/** Lucide `layers` — stacked sheets. Used for a PLAN (a group of workouts you
 *  rotate through). A folder glyph said "filing cabinet": somewhere things are
 *  stored and forgotten. A plan is the opposite - an ordered stack you work
 *  through - and stacked layers carry that without needing a label. */
export function LayersIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" />
      <path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" />
      <path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" />
    </IconBase>
  );
}

/** Lucide `pin` — the pushpin for "pin this lift to the dashboard" (#7).
 *  `filled` renders a solid pin (pinned state) vs the outline (unpinned);
 *  the surrounding button carries the accessible label / pressed state. */
export function PinIcon({
  className,
  filled = false,
}: IconProps & { filled?: boolean }) {
  return (
    <IconBase className={className}>
      <path d="M12 17v5" />
      <path
        d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z"
        fill={filled ? "currentColor" : "none"}
      />
    </IconBase>
  );
}

/** Lucide calendar — small rectangle with a top bar + two binding
 *  marks. Used as the trigger icon on the custom DatePicker
 *  (src/components/date-picker.tsx) — replaces the native
 *  `<input type="date">` chrome that looked out-of-theme. */
export function CalendarIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </IconBase>
  );
}

/** Flashlight (Lucide `flashlight`) — the LIGHT-theme state of the
 *  header theme toggle ("light on"). */
export function FlashlightIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M12 13v1" />
      <path d="M17 2a1 1 0 0 1 1 1v4a3 3 0 0 1-.6 1.8l-.6.8A4 4 0 0 0 16 12v8a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2v-8a4 4 0 0 0-.8-2.4l-.6-.8A3 3 0 0 1 6 7V3a1 1 0 0 1 1-1z" />
      <path d="M6 6h12" />
    </IconBase>
  );
}

/** Flashlight-off (Lucide `flashlight-off`) — the DARK-theme state of
 *  the header theme toggle ("light off" = the current Black/Orange). */
export function FlashlightOffIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M11.652 6H18" />
      <path d="M12 13v1" />
      <path d="M16 16v4a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-8a4 4 0 0 0-.8-2.4l-.6-.8A3 3 0 0 1 6 7V6" />
      <path d="m2 2 20 20" />
      <path d="M7.649 2H17a1 1 0 0 1 1 1v4a3 3 0 0 1-.6 1.8l-.6.8a4 4 0 0 0-.55 1.007" />
    </IconBase>
  );
}

/** Lucide `sliders-horizontal` - three rows with a handle on each. The
 *  Settings list's "General" row: app behaviour that is neither timing
 *  nor units nor account, which a gear cannot say here because the app
 *  shell already spends SettingsIcon on this whole page. A gear on a row
 *  inside Settings would point at itself. */
export function SlidersIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M21 4h-7" />
      <path d="M10 4H3" />
      <path d="M21 12h-9" />
      <path d="M8 12H3" />
      <path d="M21 20h-5" />
      <path d="M12 20H3" />
      <path d="M14 2v4" />
      <path d="M8 10v4" />
      <path d="M16 18v4" />
    </IconBase>
  );
}

/** Lucide `user` - head and shoulders. The Settings list's "Account"
 *  row. Deliberately NOT UserCheckIcon or UserXIcon: those two already
 *  mean accept-this-coach and decline-this-coach elsewhere, and a third
 *  meaning on the same silhouette would blunt both. */
export function UserIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </IconBase>
  );
}

/** Lucide `key-round` - the Settings list's "Sign-in methods" row. The
 *  bit is filled rather than stroked, which is what makes it read as a
 *  key rather than a ring at 18px. */
export function KeyIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z" />
      <circle cx="16.5" cy="7.5" r=".5" fill="currentColor" />
    </IconBase>
  );
}

/** Lucide `triangle-alert` - the Settings list's "Danger zone" row, and
 *  the only icon on that list that carries colour. It inherits
 *  text-danger from the row, matching the left rail, because the group
 *  it marks is the one whose actions cannot be undone. */
export function AlertTriangleIcon({ className }: IconProps) {
  return (
    <IconBase className={className}>
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </IconBase>
  );
}

/** Flask — the beta-features group in Settings. Only an admin ever sees it. */
export function FlaskIcon({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M10 2v6.5L4.8 17.4A2 2 0 0 0 6.5 20.5h11a2 2 0 0 0 1.7-3.1L14 8.5V2" />
      <path d="M8.5 2h7" />
      <path d="M7 15h10" />
    </svg>
  );
}

/** Plus — "add an exercise" in the workout kebab. */
export function PlusIcon({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      className={className}
      aria-hidden
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

/** Skip forward — "skip this exercise". An arrow into a wall rather than a
 *  cross: skipping moves past something, it does not delete it. */
export function SkipIcon({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M4 12h12" />
      <path d="M12 7l5 5-5 5" />
      <path d="M20 5v14" />
    </svg>
  );
}
